import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { entityResolutionApi } from '@/services/api/entity-resolution'

const RECALC_POLL_MS = 1500
const RECALC_MAX_POLLS = 600

/**
 * Trigger a full recalculate of the automatic name-based review groups: the
 * backend drops the existing name groups and rebuilds them from scratch (via
 * resolve_into_groups), leaving on-demand searches untouched. Runs as a polled
 * background job; we poll it to completion, then refresh the resolution surface
 * and the graph.
 */
export function useRecalculateClusters(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const job = await entityResolutionApi.recalculate(caseId)
      let current = job
      for (let i = 0; current.status === 'running' && i < RECALC_MAX_POLLS; i++) {
        await new Promise(resolve => setTimeout(resolve, RECALC_POLL_MS))
        current = await entityResolutionApi.getRecalculateStatus(caseId, current.job_id)
      }
      if (current.status === 'running') throw new Error('Recalculate timed out')
      if (current.status === 'failed') throw new Error(current.error || 'Recalculate failed')
      return current
    },
    onSuccess: job => {
      const found = job.result?.groups_created ?? 0
      toast.success(`Recalculated — ${found} possible-duplicate group(s)`)
    },
    onError: (err: Error) => toast.error(err.message || 'Recalculate failed'),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['case', caseId, 'entity-resolution'] })
      queryClient.invalidateQueries({ queryKey: ['case', caseId, 'graph'] })
    },
  })
}
